/* eslint-disable react-hooks/exhaustive-deps */
import CachedIcon from "@mui/icons-material/Cached";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
    styled,
    tableCellClasses,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { config, ERoles, formatNumberWithComma } from "../../config";
import useAuth from "../../hooks/useAuth";
import { clearErrorMessagesForCampaignMIS, resetUpdateCampaignReportStates } from "../../redux/MISReportsManagement/misReportsSlice";
import {
    getActiveOrgsForResellerThunk,
    getCampaignMISReportsByFilter,
    getMISReportsThunk,
    updateCampaignMISReports,
} from "../../redux/MISReportsManagement/misReportsThunk";
import TablePaginationShowPage from "../../utils/tablePagination";
import AlertMessageComponent from "../components/AlertMessageComponent";
import FetchingSpinner from "../components/FetchingSpinner";
import NoRecords from "../components/NoRecords";
import UpdateMISReportsComponent from "./UpdateMISReportsComponent";
import { apiGatewayAxiosInstance } from "../../utils/axios";
import { Download, Search } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchableDropdown from "./DropDownWithSearch";
import RefreshButton from "../../utils/refreshButton";

export const calculateTime = value => {
    const time = new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });
    const day = new Date(value).toLocaleDateString([], { day: "2-digit" });
    const month = new Date(value).toLocaleDateString([], { month: "2-digit" });
    const year = new Date(value).toLocaleDateString([], { year: "numeric" });

    return (
        <Typography className="tableContentFontForDateAndTime">
            {day}-{month}-{year} <br />
            {time}
        </Typography>
    );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 16,
        backgroundColor: theme.palette.common.lightBlue,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        border: "none",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
    },

    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderCollapse: "separate",
}));

const CampaignMIS = () => {
    const { user } = useAuth();
    const state = useSelector(state => state.misReportsSlice);
    const now = dayjs();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useState({
        botName: "",
        campaignName: "",
        templateName: "",
    });

    const [updateReport, setUpdateReport] = useState({
        openModel: false,
        startDate: dayjs().subtract(7, "days"),
        endDate: dayjs(),
    });

    const [executedAtStartDate, setExecutedAtStartDate] = useState(
        dayjs().startOf("month").hour(now.hour()).minute(now.minute()).second(now.second()),
    );
    const [executedAtEndDate, setExecutedAtEndDate] = useState(dayjs());

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);

    const [reload, setReload] = useState(false);
    const [errorResponse, setErrorResponse] = useState("");
    const [selectedClient, setSelectedClient] = useState("All");
    const [activeReseller, setActiveReseller] = useState("");
    const [selectedReseller, setSelectedReseller] = useState("");
    const [availableClients, setAvailableClients] = useState({});
    const [sumCampaignCounts, setSumCampaignCounts] = useState({
        totalRCS: 0,
        sentRCS: 0,
        deliveredRCS: 0,
        readRCS: 0,
        pendingRCS: 0,
        failedRCS: 0,
        sentRevokedRCS: 0,
        totalSMS: 0,
        sentSMS: 0,
        deliveredSMS: 0,
        failedSMS: 0,
    });
    const [loadDownload, setLoadDownload] = useState(false);

    const getTotalCampaignMISCounts = campaignReports => {
        if (campaignReports.length > 0) {
            const sumOfCounts = campaignReports.reduce(
                (acc, item) => {
                    acc.totalRCS += item.totalRCS;
                    acc.sentRCS += item.sentRCS;
                    acc.deliveredRCS += item.deliveredRCS;
                    acc.readRCS += item.readRCS;
                    acc.pendingRCS += item.pendingRCS;
                    acc.failedRCS += item.failedRCS;
                    acc.sentRevokedRCS += item.sentRevokedRCS;
                    acc.totalSMS += item.totalSMS;
                    acc.sentSMS += item.sentSMS;
                    acc.deliveredSMS += item.deliveredSMS;
                    acc.failedSMS += item.failedSMS;
                    return acc;
                },
                {
                    totalRCS: 0,
                    sentRCS: 0,
                    deliveredRCS: 0,
                    readRCS: 0,
                    pendingRCS: 0,
                    failedRCS: 0,
                    sentRevokedRCS: 0,
                    totalSMS: 0,
                    sentSMS: 0,
                    deliveredSMS: 0,
                    failedSMS: 0,
                },
            );
            setSumCampaignCounts(sumOfCounts);
        }
    };

    const handleCloseUpdateReport = () => {
        dispatch(resetUpdateCampaignReportStates());
        setUpdateReport({ ...updateReport, openModel: false });
    };

    const handleClearUpdateReport = () => {
        setUpdateReport({
            openModel: false,
            startDate: dayjs().subtract(7, "days"),
            endDate: dayjs(),
        });
    };

    const handleChangeSearchParams = event => {
        const { name, value } = event.target;
        setSearchParams({ ...searchParams, [name]: value });
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setPageSize(parseInt(event.target.value));
        setCurrentPage(0);
    };

    const onCloseErrorMessage = ({ fieldName }) => {
        dispatch(clearErrorMessagesForCampaignMIS({ fieldName }));
    };

    const reloadList = () => {
        setReload(!reload);
    };

    const handleClear = () => {
        setSearchParams({
            botName: "",
            campaignName: "",
            templateName: "",
        });
        dispatch(resetUpdateCampaignReportStates());
        setExecutedAtStartDate(dayjs());
        setExecutedAtEndDate(dayjs());
        setReload(!reload);
    };

    const handleUpdate = () => {
        dispatch(
            updateCampaignMISReports({
                executedAtStartDate: updateReport.startDate,
                executedAtEndDate: updateReport.endDate,
            }),
        );
    };

    const getActiveReseller = async () => {
        try {
            const response = await apiGatewayAxiosInstance.get(`/user_management/reseller/get-active-reseller`);
            setActiveReseller(response.data.data);
        } catch (error) {
            console.error("Error fetching active reseller:", error);
        }
    };

    const getActiveClients = async () => {
        try {
            const response = await apiGatewayAxiosInstance.get(`/user_management/organization/get-all-active-org-by-registryId`, {
                params: {
                    registryId: selectedReseller,
                },
            });
            setAvailableClients(response.data.data);
        } catch (error) {
            console.error("Error fetching active reseller:", error);
        }
    };

    const downloadCSV = async () => {
        try {
            setLoadDownload(true);

            const response = await apiGatewayAxiosInstance.get("/mis_server/campaign/download-campaign-mis-report", {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                params: {
                    roleName: user?.roleName,
                    orgId: selectedClient,
                    registryId: selectedReseller,
                    executedAtStartDate: new Date(executedAtStartDate),
                    executedAtEndDate: new Date(executedAtEndDate),
                    campaignName: searchParams.campaignName,
                    botName: searchParams.botName,
                    templateName: searchParams.templateName,
                },
            });

            if (response.status === 200) {
                const csvBlob = new Blob([response.data.data], {
                    type: "text/csv",
                });
                const csvUrl = URL.createObjectURL(csvBlob);

                const link = document.createElement("a");
                link.href = csvUrl;
                link.setAttribute("download", `campaign_mis.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Error downloading CSV: ", error);
        }
        setLoadDownload(false);
    };

    useEffect(() => {
        if (user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN) {
            // if (selectedReseller) {
            if (!selectedClient && selectedReseller) {
                getActiveClients();
            }
            const registryId = selectedReseller && selectedReseller !== "All" ? selectedReseller : null;
            const orgId = selectedClient && selectedClient !== "All" ? selectedClient : null;
            dispatch(
                getCampaignMISReportsByFilter({
                    roleName: user?.roleName,
                    currentPage: currentPage + 1,
                    pageSize,
                    orgId,
                    registryId,
                    campaignName: searchParams.campaignName,
                    botName: searchParams.botName,
                    templateName: searchParams.templateName,
                    executedAtStartDate: new Date(executedAtStartDate),
                    executedAtEndDate: new Date(executedAtEndDate),
                }),
            );
            // } else {
            //     getActiveReseller();
            // }
        } else if (user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN) {
            if (selectedClient) {
                dispatch(
                    getCampaignMISReportsByFilter({
                        roleName: user?.roleName,
                        currentPage: currentPage + 1,
                        pageSize,
                        orgId: selectedClient,
                        registryId: selectedReseller,
                        campaignName: searchParams.campaignName,
                        botName: searchParams.botName,
                        templateName: searchParams.templateName,
                        executedAtStartDate: new Date(executedAtStartDate),
                        executedAtEndDate: new Date(executedAtEndDate),
                    }),
                );
            } else {
                dispatch(getActiveOrgsForResellerThunk());
            }
        } else {
            dispatch(
                getCampaignMISReportsByFilter({
                    currentPage: currentPage + 1,
                    pageSize,
                    orgId: user?.orgId,
                    campaignName: searchParams.campaignName,
                    botName: searchParams.botName,
                    templateName: searchParams.templateName,
                    executedAtStartDate: new Date(executedAtStartDate),
                    executedAtEndDate: new Date(executedAtEndDate),
                }),
            );
        }
    }, [currentPage, pageSize, reload, selectedClient, selectedReseller]);

    useEffect(() => {
        getActiveReseller();
        getTotalCampaignMISCounts(state.campaignMISReports);
    }, [state.getCampaignMISReporertsLoading]);

    return (
        <Grid>
            <Grid container mb={4} className="listHeader">
                <Grid item md={6} className="align-items-center">
                    <Typography variant="h3">
                        <b>Campaign MIS</b>
                    </Typography>
                </Grid>

                <Grid item md={6} className="listHeaderFilter">
                    <Grid display={"flex"} flexDirection={"row"} gap={2}>
                        <RefreshButton loading={state.getCampaignMISReporertsLoading} onClick={reloadList} />
                        {(user?.roleName === ERoles.RESELLER_ADMIN || user?.roleName === ERoles.SUPER_ADMIN) && (
                            <Button
                                sx={{ marginRight: "10px" }}
                                variant="contained"
                                onClick={() => setUpdateReport({ ...updateReport, openModel: true })}
                            >
                                Update
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Card className="border border-secondary-50">
                <CardContent
                    sx={{
                        padding: 0,
                    }}
                >
                    {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && (
                        <Grid
                            sx={{
                                padding: 5,
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                                "@media (max-width: 600px)": {
                                    flexDirection: "column",
                                },
                            }}
                        >
                            <SearchableDropdown
                                label="Select Client"
                                placeholder="Clients"
                                name="Active Clients"
                                size="small"
                                disabled={state.activeOrgLoading}
                                dropDownList={state.activeOrgList || []}
                                value={selectedClient || "All"}
                                handleChange={e => {
                                    const value = e.target.value;
                                    setCurrentPage(0);
                                    setPageSize(10);
                                    setSelectedClient(value);
                                }}
                                valueKey={"_id"}
                                dropDownValueKey={"orgName"}
                            />
                        </Grid>
                    )}

                    {user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN && (
                        <Grid
                            sx={{
                                padding: 5,
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                                "@media (max-width: 600px)": {
                                    flexDirection: "column",
                                },
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <SearchableDropdown
                                    label="Select Reseller"
                                    placeholder="Reseller"
                                    name="Active Reseller"
                                    size="small"
                                    disabled={state.activeOrgLoading}
                                    value={selectedReseller || "All"}
                                    dropDownList={activeReseller || []}
                                    handleChange={e => {
                                        const value = e.target.value;
                                        setCurrentPage(0);
                                        setPageSize(10);
                                        setSelectedReseller(value === "All" ? "" : value);
                                        if (value === "All") {
                                            setSelectedClient("All");
                                        } else {
                                            setSelectedClient("");
                                        }
                                    }}
                                    valueKey={"_id"}
                                    dropDownValueKey={"resellerName"}
                                />
                            </FormControl>

                            {config.REACT_APP_TO_SHOW_MIS_REPORTS_TO_SUPER_ADMIN === "true" && (
                                <FormControl size="small" fullWidth>
                                    {/* <InputLabel id="client-label">Select Client </InputLabel>
                                    <Select
                                        disabled={state.activeOrgLoading || !selectedReseller}
                                        name="Active Clients"
                                        labelId="client-label"
                                        id="client-label"
                                        // value={selectedClient ? selectedClient : "All"}
                                        value={selectedClient || "All"}
                                        onChange={e => {
                                            const value = e.target.value;
                                            setCurrentPage(0);
                                            setPageSize(10);
                                            setSelectedClient(value);
                                        }}
                                        label="Select Client"
                                        size="small"
                                        props={{
                                            PaperProps: {
                                                sx: {
                                                    boxShadow: " 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="All">
                                            <Grid
                                                container
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <Grid item md={6} display={"flex"} flexDirection={"column"}>
                                                    <Typography sx={{ fontSize: "0.9rem", fontWeight: "600" }}>All</Typography>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                        {availableClients &&
                                            availableClients?.length > 0 &&
                                            availableClients?.map((org, index) => (
                                                <MenuItem key={index} value={org._id}>
                                                    <Grid
                                                        container
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                        }}
                                                    >
                                                        <Grid item md={6} display={"flex"} flexDirection={"column"}>
                                                            <Typography sx={{ fontSize: "0.9rem", fontWeight: "600" }}>{org?.orgName}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </MenuItem>
                                            ))}
                                    </Select> */}
                                    <SearchableDropdown
                                        label="Select Clients"
                                        placeholder="Clients"
                                        name="Active Clients"
                                        size="small"
                                        disabled={state.activeOrgLoading || !selectedReseller}
                                        value={selectedClient || "All"}
                                        dropDownList={availableClients || []}
                                        handleChange={e => {
                                            const value = e.target.value;
                                            setCurrentPage(0);
                                            setPageSize(10);
                                            setSelectedClient(value);
                                        }}
                                        valueKey={"_id"}
                                        dropDownValueKey={"orgName"}
                                    />
                                </FormControl>
                            )}
                        </Grid>
                    )}

                    {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && !selectedClient ? (
                        <Typography color="black" fontWeight={500} textAlign={"center"}>
                            Select Client
                        </Typography>
                    ) : (
                        <>
                            <Grid
                                sx={{
                                    // display: "flex",
                                    // padding: 3,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexDirection: "row",
                                    gap: 2,
                                    padding: 5,
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 5,
                                    }}
                                >
                                    <Grid>
                                        <TextField
                                            size="small"
                                            label="Campaign Name"
                                            id="campaignName"
                                            placeholder="Campaign Name"
                                            name="campaignName"
                                            value={searchParams.campaignName}
                                            onChange={handleChangeSearchParams}
                                        />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            size="small"
                                            label="Bot Name"
                                            id="botName"
                                            placeholder="Bot Name"
                                            name="botName"
                                            value={searchParams.botName}
                                            onChange={handleChangeSearchParams}
                                        />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            size="small"
                                            label="Template Name"
                                            id="templateName"
                                            placeholder="Template Name"
                                            name="templateName"
                                            value={searchParams.templateName}
                                            onChange={handleChangeSearchParams}
                                        />
                                    </Grid>
                                    <Grid>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Start Date"
                                                value={executedAtStartDate}
                                                onChange={value => setExecutedAtStartDate(dayjs(value))}
                                                format="DD-MM-YYYY"
                                                name="startDate"
                                                disableFuture
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        height: "36px", // Adjust height for DatePicker
                                                        padding: "0 10px", // Adjust padding
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="End Date"
                                                value={executedAtEndDate}
                                                onChange={value => setExecutedAtEndDate(dayjs(value))}
                                                format="DD-MM-YYYY"
                                                name="startDate"
                                                disableFuture
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        height: "36px", // Adjust height for DatePicker
                                                        padding: "0 10px", // Adjust padding
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                                <Grid
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Button
                                        onClick={handleClear}
                                        color="error"
                                        variant="contained"
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 1,
                                            padding: "6px 16px", // Adjust button padding
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 1,
                                            padding: "6px 16px", // Adjust button padding
                                        }}
                                        onClick={() => setReload(!reload)}
                                    >
                                        Search
                                    </Button>

                                    <Button onClick={downloadCSV} disabled={loadDownload || state?.campaignMISReports?.length <= 0}>
                                        {loadDownload ? <CircularProgress size={15} className="spinner" /> : <DownloadIcon />}
                                        &nbsp; Download Report
                                    </Button>
                                </Grid>
                            </Grid>

                            {state.getCampaignMISReporertsLoading ? (
                                <FetchingSpinner />
                            ) : state.campaignMISReports && state?.campaignMISReports?.length > 0 ? (
                                <StyledTableContainer component={Paper} className="table-container" sx={{ maxHeight: "70vh" }}>
                                    <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Sr No
                                                </StyledTableCell>
                                                {user?.roleName === ERoles.SUPER_ADMIN && (
                                                    <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                        <Typography>Reseller Name</Typography>
                                                    </StyledTableCell>
                                                )}
                                                {/* {(!selectedClient || selectedClient === "All") && !(user?.roleName === ERoles.ORG_ADMIN) && ( */}
                                                {(user?.roleName === ERoles.SUPER_ADMIN || user?.roleName === ERoles.RESELLER_ADMIN) && (
                                                    <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                        <Typography>Organization Name</Typography>
                                                    </StyledTableCell>
                                                )}
                                                {/* {user?.roleName === ERoles.SUPER_ADMIN && !selectedReseller && ( */}

                                                <StyledTableCell className="tableHeaderFont" align="left">
                                                    Campaign Name
                                                </StyledTableCell>
                                                {/* <StyledTableCell className="tableHeaderFont" align="left">
                                                    Other Details
                                                </StyledTableCell> */}
                                                <StyledTableCell className="tableHeaderFont" align="left">
                                                    Bot Name
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="left">
                                                    Template Name
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    ExecutedAt
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Total RCS
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Sent
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Delivered
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Read
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Pending
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Failed
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Sent Revoked
                                                </StyledTableCell>
                                                {/* <StyledTableCell className="tableHeaderFont" align="center">
                                                    Total SMS
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Sent
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Delivered
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Failed
                                                </StyledTableCell> */}
                                                {/* <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS

                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS
                                                </StyledTableCell> */}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {state.campaignMISReports.length > 0 &&
                                                state.campaignMISReports?.map((campaign, index) => (
                                                    <StyledTableRow key={index}>
                                                        <StyledTableCell align="center">{currentPage * pageSize + index + 1}</StyledTableCell>
                                                        {/* {(!selectedClient || selectedClient === "All") && !(user?.roleName === ERoles.ORG_ADMIN) && ( */}
                                                        {user?.roleName === ERoles.SUPER_ADMIN && (
                                                            <StyledTableCell align="left">{campaign.resellerName}</StyledTableCell>
                                                        )}
                                                        {(user?.roleName === ERoles.SUPER_ADMIN || user?.roleName === ERoles.RESELLER_ADMIN) && (
                                                            <StyledTableCell align="left">{campaign.orgName}</StyledTableCell>
                                                        )}
                                                        {/* {user?.roleName === ERoles.SUPER_ADMIN && !selectedReseller && ( */}

                                                        <StyledTableCell align="left">
                                                            <Typography sx={{ lineBreak: "auto", wordBreak: "break-word" }}>
                                                                {campaign.campaignName}
                                                            </Typography>
                                                        </StyledTableCell>

                                                        <StyledTableCell align="left">
                                                            <Typography sx={{ lineBreak: "auto", wordBreak: "break-word" }}>
                                                                {campaign.botName ?? "---"}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="left">
                                                            <Typography sx={{ lineBreak: "auto", wordBreak: "break-word" }}>
                                                                {campaign.templateName}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            <Typography sx={{ lineBreak: "auto", wordBreak: "break-word" }}>
                                                                {campaign.executedAt ? calculateTime(campaign.executedAt) : "-"}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.totalRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.sentRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.deliveredRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.readRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.pendingRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.failedRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.sentRevokedRCS)}
                                                        </StyledTableCell>
                                                        {/* <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.totalSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.sentSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.deliveredSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(campaign.failedSMS)}
                                                        </StyledTableCell> */}
                                                    </StyledTableRow>
                                                ))}
                                        </TableBody>
                                        <TableFooter
                                            className="sticky-footer"
                                            sx={{
                                                position: "sticky",
                                                bottom: 0,
                                                backgroundColor: "red",
                                                padding: 0,
                                                margin: 0,
                                            }}
                                        >
                                            <TableRow>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {"Total: "}
                                                </StyledTableCell>

                                                {(user?.roleName === ERoles.SUPER_ADMIN || user?.roleName === ERoles.RESELLER_ADMIN) && (
                                                    <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                )}

                                                {user?.roleName === ERoles.SUPER_ADMIN && (
                                                    <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                )}
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.totalRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.sentRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.deliveredRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.readRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.pendingRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.failedRCS)}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumCampaignCounts.sentRevokedRCS)}
                                                </StyledTableCell>
                                                {/* <StyledTableCell className="tableHeaderFont" align="center">
                                                    {sumCampaignCounts.totalSMS}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {sumCampaignCounts.sentSMS}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {sumCampaignCounts.deliveredSMS}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {sumCampaignCounts.failedSMS}
                                                </StyledTableCell> */}
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </StyledTableContainer>
                            ) : (
                                <NoRecords />
                            )}

                            {state.campaignMISReportsTotalCount > 0 && (
                                <Grid
                                    className="my-2"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: 10,
                                    }}
                                >
                                    <Grid
                                        style={{
                                            marginTop: "15px",
                                        }}
                                    >
                                        Total Campaigns: {state.campaignMISReportsTotalCount}
                                    </Grid>

                                    <TablePaginationShowPage
                                        rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
                                        colSpan={3}
                                        count={state.campaignMISReportsTotalCount}
                                        rowsPerPage={pageSize}
                                        page={currentPage}
                                        slotProps={{
                                            select: {
                                                "aria-label": "Rows per page",
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageRoundedIcon,
                                                    lastPageIcon: LastPageRoundedIcon,
                                                    nextPageIcon: ChevronRightRoundedIcon,
                                                    backPageIcon: ChevronLeftRoundedIcon,
                                                },
                                            },
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                    <AlertMessageComponent
                                        message={errorResponse}
                                        fieldName={"ErrorMessage"}
                                        handleClose={() => setErrorResponse("")}
                                        show={Boolean(errorResponse)}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    {updateReport.openModel && (
                        <UpdateMISReportsComponent
                            open={updateReport.openModel}
                            handleClose={handleCloseUpdateReport}
                            setUpdateDates={setUpdateReport}
                            updateDates={updateReport}
                            handleClear={handleClearUpdateReport}
                            handleUpdate={handleUpdate}
                            loading={state.updateCampaignMISReportsLoading}
                            error={state.updateCampaignMISReportsError}
                            success={state.updateCampaignMISReports}
                        />
                    )}
                    {state.getCampaignMISReportsError && (
                        <AlertMessageComponent
                            message={state.getCampaignMISReportsError}
                            fieldName={"getCampaignMISReportsError"}
                            handleClose={onCloseErrorMessage}
                            show={Boolean(state.getCampaignMISReportsError)}
                        />
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default CampaignMIS;
